import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, FileText, Image as ImageIcon, Users, CheckCircle2, Circle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const COMMUNITY_URL = "https://www.facebook.com/groups/brandsnap.pro/";

const stepLink = {
  upload_photos: "/app/step1",
  brand_profile: "/app/step2",
  get_prompts: "/app/step3",
  generate_images: "/app/step4",
  upload_results: "/app/step5",
  your_gallery: "/app/step6",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState({ steps: [], stats: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/progress").then((r) => {
      setProgress(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const firstName = (user?.full_name || "there").split(" ")[0];
  const isFirstLogin = progress.stats?.reference_photos === 0 && progress.stats?.gallery_images === 0;
  const completedSteps = progress.steps?.filter((s) => s.complete).length || 0;
  const totalSteps = progress.steps?.length || 6;
  const pct = Math.round((completedSteps / totalSteps) * 100);

  return (
    <div className="max-w-6xl mx-auto" data-testid="dashboard-home">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Member dashboard</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Welcome back, {firstName}.</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-2xl">Here's where you are in the system, and what to do next.</p>

      {isFirstLogin && (
        <Link to="/app/system" className="block mt-8 bsp-card bsp-card-hover p-6 border-l-4 border-[#D4A853]" data-testid="welcome-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="eyebrow">New here?</div>
              <h3 className="font-display text-2xl text-[#1A1F3D] mt-1">Welcome! Start Here →</h3>
              <p className="text-[#52525b] mt-1">Take 4 minutes to watch the walkthrough, then complete Step 1.</p>
            </div>
            <ArrowRight className="w-6 h-6 text-[#1A1F3D]" />
          </div>
        </Link>
      )}

      {/* Progress tracker */}
      <div className="mt-10 bsp-card p-6 md:p-8" data-testid="progress-tracker">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow">Your progress</span>
            <h2 className="font-display text-2xl text-[#1A1F3D] mt-1">{completedSteps} of {totalSteps} steps complete</h2>
          </div>
          <div className="text-3xl font-display text-[#D4A853]">{pct}%</div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-[#F5E6E0] overflow-hidden">
          <div className="h-full bg-[#D4A853] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {progress.steps?.map((s, i) => {
            const Icon = s.complete ? CheckCircle2 : Circle;
            return (
              <Link
                to={stepLink[s.key] || "/app"}
                key={s.key}
                className="text-left p-4 rounded-lg border border-[#e5e7eb] hover:border-[#D4A853] hover:bg-[#FAFAF8] transition-all"
                data-testid={`progress-step-${s.key}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#52525b]">STEP {i + 1}</span>
                  <Icon className={`w-4 h-4 ${s.complete ? "text-[#D4A853]" : "text-[#cbd5e1]"}`} />
                </div>
                <div className={`mt-2 text-sm font-medium ${s.complete ? "text-[#1A1F3D]" : "text-[#2D2D2D]"}`}>{s.label}</div>
                <div className="mt-1 text-xs text-[#52525b]">{s.complete ? "Complete" : "Not started"}</div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <StatCard icon={Camera} label="Reference photos uploaded" value={progress.stats?.reference_photos || 0} testid="stat-ref" />
        <StatCard icon={FileText} label="Prompts copied" value={progress.stats?.prompts_copied || 0} testid="stat-prompts" />
        <StatCard icon={ImageIcon} label="Images in gallery" value={progress.stats?.gallery_images || 0} testid="stat-gallery" />
      </div>

      {/* Community */}
      <a
        href={COMMUNITY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-8 block bsp-card bsp-card-hover p-7 bg-[#1A1F3D] text-white"
        data-testid="community-card"
      >
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-[#D4A853]/20 flex items-center justify-center">
            <Users className="w-6 h-6 text-[#D4A853]" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-display text-2xl">Join the Community</h3>
            <p className="text-white/70 mt-1">Connect with other members, share your wins, and get inspired.</p>
          </div>
          <span className="px-5 py-2.5 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold inline-flex items-center gap-2">
            Open community <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </a>

      {loading && <div className="text-[#52525b] text-sm mt-4">Loading…</div>}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bsp-card p-6 flex items-center gap-4" data-testid={testid}>
    <div className="w-11 h-11 rounded-xl bg-[#F5E6E0] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#1A1F3D]" strokeWidth={1.6} />
    </div>
    <div>
      <div className="font-display text-3xl text-[#1A1F3D]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#52525b] font-medium">{label}</div>
    </div>
  </div>
);
